import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiGet } from "../../api";
import { Button } from "../../components/ui/button";
import { Copy, Plus, Trash2 } from "lucide-react";

/** Settings tab: add supplier + manage categories + category owners + records updater.
 *  The ICM / warehouse SKU list is managed on the main Settings page (single source of
 *  truth — same warehouse_skus DB doc / endpoints); it is intentionally not duplicated here. */
export default function SettingsTab() {
  return (
    <div data-testid="settings-tab" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <AddSupplierSection />
      <CategoriesSection />
      <CategoryOwnersSection />
      <RecordsUpdaterSection />
    </div>
  );
}


function AddSupplierSection() {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [categories, setCategories] = useState([]);
  const [standardSuppliers, setStandardSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    apiGet("/portal/admin/categories-allowed").then(d => setCategories(d?.categories || [])).catch(() => {});
    apiGet("/standard-suppliers").then(d => setStandardSuppliers(d?.suppliers || [])).catch(() => {});
  }, []);

  const portalUrl = created ? `${window.location.origin}/portal?token=${created.token}` : "";

  const handleSubmit = async () => {
    if (!name.trim() || !category.trim() || !contactEmail.trim()) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portal/admin/suppliers`, {
        name: name.trim(), category: category.trim(), contact_email: contactEmail.trim(),
      });
      setCreated(res.data);
      setName(""); setCategory(""); setContactEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add supplier");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ fontSize: "1.05rem", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        <Plus size={16} style={{ display: "inline", marginRight: "0.4rem" }} />Add new supplier
      </h3>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.5rem", opacity: 0.8 }}>
        Creates a supplier record with an auto-generated token. The portal URL appears below — copy it and email it to the supplier contact.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem", maxWidth: "800px" }}>
        <div>
          <label className="form-label">Supplier Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            list="portal-standard-suppliers"
            autoComplete="off"
            data-testid="new-supplier-name"
            placeholder="Start typing — e.g. Altecnic"
          />
          <datalist id="portal-standard-suppliers">
            {standardSuppliers.map(s => <option key={s} value={s} />)}
          </datalist>
          <p style={{ fontSize: "0.7rem", color: "var(--octopus-voltage)", opacity: 0.6, marginTop: "0.25rem" }}>
            Autocompletes from {standardSuppliers.length} standard suppliers. Custom names allowed.
          </p>
        </div>
        <div>
          <label className="form-label">Category *</label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="form-input"
            list="portal-categories"
            data-testid="new-supplier-category"
            placeholder="e.g. Electrical"
          />
          <datalist id="portal-categories">
            {categories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label className="form-label">Contact Email *</label>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="form-input" data-testid="new-supplier-email" placeholder="contact@example.com" />
        </div>
      </div>

      <Button onClick={handleSubmit} disabled={submitting} className="btn btn-primary" data-testid="add-supplier-btn">
        {submitting ? "Adding…" : "Add Supplier"}
      </Button>

      {created && (
        <div data-testid="supplier-created-banner" style={{ marginTop: "1.5rem", padding: "1rem", background: "rgba(24,240,184,0.08)", border: "1px solid rgba(24,240,184,0.3)", borderRadius: "8px" }}>
          <p style={{ color: "var(--octopus-ice)", marginBottom: "0.5rem" }}>
            <strong>{created.name}</strong> added. Share this link with your contact:
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <code style={{ flex: 1, minWidth: "250px", padding: "0.5rem 0.75rem", background: "rgba(10,18,42,0.6)", borderRadius: "4px", fontSize: "0.85rem", wordBreak: "break-all" }}>
              {portalUrl}
            </code>
            <Button onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Copied"); }} className="btn btn-secondary" data-testid="copy-new-supplier-url">
              <Copy size={12} style={{ marginRight: "0.3rem" }} /> Copy
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/** Manage the dynamic categories table. Add new entries + delete (blocked
 *  if any supplier is using the category). */
function CategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/portal/admin/categories-allowed");
      setCategories(d?.categories || []);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error("Enter a category name"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portal/admin/categories`, { name: newName.trim() });
      setCategories(res.data.categories || []);
      setNewName("");
      toast.success(`Added "${newName.trim()}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete category "${name}"? This will also clear its owner mapping.`)) return;
    try {
      const res = await axios.delete(`${API}/portal/admin/categories/${encodeURIComponent(name)}`);
      setCategories(res.data.categories || []);
      toast.success(`Deleted "${name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="card" data-testid="categories-section">
      <h3 style={{ fontSize: "1.05rem", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Categories
      </h3>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.8 }}>
        Manage the categories suppliers can be tagged with. A category can be removed only when no supplier (active or inactive) is using it.
      </p>

      {loading ? <p style={{ opacity: 0.7 }}>Loading…</p> : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }} data-testid="categories-list">
            {categories.length === 0 ? (
              <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem" }}>No categories yet.</span>
            ) : categories.map(c => (
              <span key={c} data-testid={`category-pill-${c}`}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.35rem 0.5rem 0.35rem 0.75rem",
                  background: "rgba(96,240,248,0.08)", border: "1px solid rgba(96,240,248,0.2)", borderRadius: "999px",
                  color: "var(--octopus-ice)", fontSize: "0.85rem" }}>
                {c}
                <button onClick={() => handleDelete(c)} title={`Delete ${c}`}
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "#F050F8", padding: 0, display: "inline-flex" }}
                  data-testid={`delete-category-${c}`}>
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", maxWidth: "440px" }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              className="form-input"
              placeholder="New category name"
              data-testid="new-category-input"
              style={{ flex: 1 }}
            />
            <Button onClick={handleAdd} disabled={busy || !newName.trim()} className="btn btn-primary" data-testid="add-category-btn">
              <Plus size={14} style={{ marginRight: "0.3rem" }} /> Add
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


/** Category → owner mapping. Each category has ONE owner. Saved as a single
 *  document in /api/portal/admin/category-owners. */
function CategoryOwnersSection() {
  const [owners, setOwners] = useState([]); // [{category, owner_name, owner_email}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allowed, current] = await Promise.all([
        apiGet("/portal/admin/categories-allowed"),
        apiGet("/portal/admin/category-owners"),
      ]);
      const cats = allowed?.categories || [];
      const existing = current?.owners || [];
      // Build one row per allowed category, prefilled if already configured.
      setOwners(cats.map(c => {
        const found = existing.find(o => o.category === c);
        return found
          ? { category: c, owner_name: found.owner_name || "", owner_email: found.owner_email || "" }
          : { category: c, owner_name: "", owner_email: "" };
      }));
    } catch (err) {
      toast.error("Failed to load category owners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, field, value) => {
    setOwners(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  const handleSave = async () => {
    // Only persist rows that have BOTH name + email filled (a row with just
    // a category isn't a configured owner yet).
    const payload = owners
      .filter(o => o.owner_name.trim() && o.owner_email.trim())
      .map(o => ({
        category: o.category,
        owner_name: o.owner_name.trim(),
        owner_email: o.owner_email.trim(),
      }));
    setSaving(true);
    try {
      await axios.put(`${API}/portal/admin/category-owners`, { owners: payload });
      toast.success(`Saved ${payload.length} category owner${payload.length === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" data-testid="category-owners-section">
      <h3 style={{ fontSize: "1.05rem", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Category owners
      </h3>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.8 }}>
        Each category has one Procurement Manager who receives new-submission notifications. Leave the row blank to skip a category.
      </p>

      {loading ? <p style={{ opacity: 0.7 }}>Loading…</p> : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table" data-testid="category-owners-table">
            <thead>
              <tr>
                <th style={{ minWidth: "160px" }}>Category</th>
                <th>Owner Name</th>
                <th>Owner Email</th>
              </tr>
            </thead>
            <tbody>
              {owners.map((o, idx) => (
                <tr key={o.category} data-testid={`category-row-${o.category}`}>
                  <td><strong>{o.category}</strong></td>
                  <td>
                    <input
                      value={o.owner_name}
                      onChange={(e) => update(idx, "owner_name", e.target.value)}
                      className="form-input"
                      placeholder="Full name"
                      data-testid={`owner-name-${o.category}`}
                      style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem" }}
                    />
                  </td>
                  <td>
                    <input
                      type="email"
                      value={o.owner_email}
                      onChange={(e) => update(idx, "owner_email", e.target.value)}
                      className="form-input"
                      placeholder="pm@octopus.energy"
                      data-testid={`owner-email-${o.category}`}
                      style={{ padding: "0.4rem 0.6rem", fontSize: "0.85rem" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: "1rem" }}>
        <Button onClick={handleSave} disabled={saving || loading} className="btn btn-primary" data-testid="save-category-owners-btn">
          {saving ? "Saving…" : "Save category owners"}
        </Button>
      </div>
    </div>
  );
}


/** Single global "records updater" recipient — receives every approval email. */
function RecordsUpdaterSection() {
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/portal/admin/records-updater").then(d => {
      setLabel(d?.label || "");
      setEmail(d?.email || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!label.trim() || !email.trim()) {
      toast.error("Both fields are required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/portal/admin/records-updater`, { label: label.trim(), email: email.trim() });
      toast.success("Records updater saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" data-testid="records-updater-section">
      <h3 style={{ fontSize: "1.05rem", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        Records updater
      </h3>
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1.25rem", opacity: 0.8 }}>
        One recipient (or shared inbox) receives every approved-submission email. Rejection does not trigger an email.
      </p>

      {loading ? <p style={{ opacity: 0.7 }}>Loading…</p> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1rem", maxWidth: "700px" }}>
          <div>
            <label className="form-label">Name or inbox label *</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="form-input"
              placeholder="e.g. Pricing Records"
              data-testid="records-updater-label"
            />
          </div>
          <div>
            <label className="form-label">Email address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="pricing@octopus.energy"
              data-testid="records-updater-email"
            />
          </div>
        </div>
      )}

      <Button onClick={handleSave} disabled={saving || loading} className="btn btn-primary" data-testid="save-records-updater-btn">
        {saving ? "Saving…" : "Save records updater"}
      </Button>
    </div>
  );
}
